import styles from './ServiceShowcase.module.css';
import useInView from '../Animation/useInView.ts';
import { FaChevronDown } from 'react-icons/fa';
import { useState } from 'react';

interface Service {
    id: string;
    title: string;
    price: number | null;
    hasFrom: boolean;
    unit?: string;
    imagePath?: string;
    description: string;
}

interface ServicesGridProps {
    title: string;
    subtitle?: string;
    services: Service[];
    className?: string;
}

const ServicesGrid: React.FC<ServicesGridProps> = ({
    title,
    subtitle,
    services,
    className = ''
}) => {
    const { ref, isInView } = useInView({ threshold: 0.1, triggerOnce: true });
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const toggleService = (id: string) => {
        setExpandedId(expandedId === id ? null : id);
    };

    const formatPrice = (price: number | null, hasFrom: boolean, unit?: string) => {
        if (price === null) return '';
        const prefix = hasFrom ? 'ОТ ' : '';
        const suffix = unit ? ` ${unit}` : '';
        return `${prefix}${price.toLocaleString('ru-RU')}${suffix}`;
    };

    return (
        <section id="Service" ref={ref} className={`${styles.servicesGrid} ${className} ${isInView ? styles.visible : ''}`}>
            <div className={styles.container}>
                <div className={styles.header}>
                    <h2 className={styles.title}>{title}</h2>
                    {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
                </div>

                <div className={styles.grid}>
                    {services.map((service, index) => (
                        <div
                            key={service.id}
                            className={`${styles.cardWrapper} ${expandedId === service.id ? styles.expanded : ''}`}
                            style={{ '--delay': `${0.15 + index * 0.1}s` } as React.CSSProperties}
                        >
                            <div
                                className={`${styles.cardHeader} ${expandedId === service.id ? styles.open : ''}`}
                                style={{
                                    backgroundImage: service.imagePath
                                        ? `linear-gradient(rgba(10,20,25,0.85), rgba(10,20,25,0.85)), url(${service.imagePath})`
                                        : 'none',
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    backgroundRepeat: 'no-repeat',
                                    backgroundClip: 'content-box',
                                }}
                                onClick={() => toggleService(service.id)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => e.key === 'Enter' && toggleService(service.id)}
                            >
                                <div className={styles.contentWrapper}>
                                    <div className={styles.question}>
                                        <h3 className={styles.questionText}>{service.title}</h3>
                                        {service.price !== null && (
                                            <span className={styles.price}>
                                                {formatPrice(service.price, service.hasFrom, service.unit)}
                                            </span>
                                        )}
                                    </div>
                                    <span
                                        className={`${styles.toggleIcon} ${expandedId === service.id ? styles.toggleOpen : ''}`}
                                    >
                                        <FaChevronDown />
                                    </span>
                                </div>
                            </div>

                            <div className={`${styles.cardPanel} ${expandedId === service.id ? styles.panelOpen : styles.panelClosed}`}>
                                <p>{service.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default ServicesGrid;
